import AddIcon from '@mui/icons-material/Add';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    Snackbar,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { where } from 'firebase/firestore';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../MicrospecDynamicUI/GlobalProvider';
import { getDataFromCollectionWithQuery, getDocById } from '../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessProduct } from '../../MicrospecDynamicUI/Models/Business/BusinessProduct';
import { BusinessSpecification } from '../../MicrospecDynamicUI/Models/Business/BusinessSpecification';
import { BusinessSpecificationCategory } from '../../MicrospecDynamicUI/Models/Business/BusinessSpecificationCategory';
import { useMicrospecThemeLanguageContext } from '../../MicrospecDynamicUI/Themes';
import { useCart } from '../Contexts/CartContext';
import CartDialog from '../Components/CartDialog';
import {
    CATEGORIES_COLLECTION,
    PRODUCTS_COLLECTION,
    SPECIFICATION_CATEGORIES_COLLECTION,
    SPECIFICATIONS_COLLECTION,
} from '../constants/collectionPaths';
import { cloneSpecifications, specificationMappers } from '../Utils/specificationMappers';

export interface ProductDetailPageProps {
    /** 'cartOrder' (default) adds the product to the cart; 'getQuote' opens WhatsApp with a quote request. */
    orderType?: 'cartOrder' | 'getQuote';
    /** WhatsApp number in international format without +, e.g. "972501234567". Required when orderType is 'getQuote'. */
    whatsappNumber?: string;
}

const ProductDetailPage: React.FC<ProductDetailPageProps> = ({
    orderType = 'cartOrder',
    whatsappNumber = '972500000000',
}) => {
    const { category_id, product_id } = useParams<{ category_id: string; product_id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { translate } = localizationProvider;
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const db = cloudProvider.firestore?.instance;
    const { addItem, totalItems } = useCart();

    const accentColor = theme.palette.primary.main;

    const [product, setProduct] = useState<BusinessProduct | null>(null);
    const [categoryName, setCategoryName] = useState<string>('');
    const [specCategories, setSpecCategories] = useState<BusinessSpecificationCategory[]>([]);
    // Working copies of specs — mutated by widget onChange handlers
    const [workingSpecs, setWorkingSpecs] = useState<BusinessSpecification[]>([]);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [relatedProducts, setRelatedProducts] = useState<BusinessProduct[]>([]);
    const [quantity, setQuantity] = useState(1);
    const [cartSnackbar, setCartSnackbar] = useState(false);
    const [cartDialog, setCartDialog] = useState(false);

    // Fetch product + category name + specs in parallel
    useEffect(() => {
        if (!db || !product_id) return;

        const fetchAll = async () => {
            setLoading(true);
            try {
                const [productData, cats, specs] = await Promise.all([
                    getDocById(null, null, db, PRODUCTS_COLLECTION, product_id, false),
                    category_id
                        ? getDocById(null, null, db, CATEGORIES_COLLECTION, category_id, false)
                        : Promise.resolve(null),
                    getDataFromCollectionWithQuery(null, null, db, SPECIFICATIONS_COLLECTION, [], true, 500),
                ]);

                if (!productData) {
                    setError(translate('product.productNotFound'));
                    return;
                }

                setProduct(productData as BusinessProduct);
                setSelectedImage((productData as BusinessProduct).images?.[0] ?? null);

                if (cats) {
                    setCategoryName(translate((cats as any).name) ?? '');
                }

                // Load spec categories only if product has specs
                const specIds: string[] = (productData as BusinessProduct).specification_ids ?? [];
                if (specIds.length > 0) {
                    const specCats = await getDataFromCollectionWithQuery(
                        null, null, db, SPECIFICATION_CATEGORIES_COLLECTION, [], true, 200,
                    );
                    setSpecCategories(specCats as BusinessSpecificationCategory[]);
                    const filtered = (specs as BusinessSpecification[]).filter(s => specIds.includes(s.id!));
                    setWorkingSpecs(cloneSpecifications(filtered));
                }

                if (category_id) {
                    const related = await getDataFromCollectionWithQuery(
                        null, null, db, PRODUCTS_COLLECTION,
                        [where('category_id', '==', category_id)],
                        true, 5,
                    ) as BusinessProduct[];
                    setRelatedProducts(related.filter(p => p.id !== product_id).slice(0, 4));
                }
            } catch (err: any) {
                setError(err?.message ?? translate('product.failedToLoad'));
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, product_id, category_id]);

    // ── Spec update handler ───────────────────────────────────────────────────
    const handleSpecUpdate = useCallback((specId: string, updatedCharacteristics: Record<string, any>) => {
        setWorkingSpecs(prev =>
            prev.map(s =>
                s.id === specId ? { ...s, type_characteristics: updatedCharacteristics } : s,
            ),
        );
    }, []);

    // ── Price calculations ───────────────────────────────────────────────────
    const basePrice = product?.price ?? 0;
    const additionsPrice = workingSpecs.reduce((sum, spec) => {
        const mapper = specificationMappers[spec.type];
        return sum + (mapper ? mapper.additionalPriceMapper(spec, quantity) : 0);
    }, 0);
    const totalItemPrice = basePrice * quantity + additionsPrice;

    // ── Add to cart ──────────────────────────────────────────────────────────
    const handleAddToCart = () => {
        if (!product) return;
        addItem(product, quantity, cloneSpecifications(workingSpecs));
        setCartSnackbar(true);
    };

    // ── Get quote via WhatsApp ───────────────────────────────────────────────
    const handleGetQuote = () => {
        if (!product) return;
        const name = productName || product.id;
        let msg = `שלום, אני מעוניין לקבל הצעת מחיר עבור המוצר: ${name}`;
        msg += `\nכמות: ${quantity}`;
        for (const spec of workingSpecs) {
            const mapper = specificationMappers[spec.type];
            if (!mapper) continue;
            const specName = translate(spec.name as any) ?? '';
            const specValue = mapper.valueMapper(spec);
            if (specValue) msg += `\n${specName}: ${specValue}`;
        }
        msg += '\nתודה!';
        window.open(
            `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(msg)}`,
            '_blank',
            'noopener,noreferrer',
        );
    };

    const productName = product ? (translate(product.name as any) ?? '') : '';
    const productDescription = product?.description ? (translate(product.description as any) ?? '') : '';
    const quantityLabel = product?.quantity_label ? (translate(product.quantity_label as any) ?? '') : '';

    const priceDisplay =
        product?.price != null
            ? `₪${product.price.toFixed(2)}${quantityLabel ? ` / ${quantityLabel}` : ''}`
            : null;

    // ── Group working specs by category ──────────────────────────────────────
    const workingSpecsByCat: Record<string, BusinessSpecification[]> = {};
    for (const spec of workingSpecs) {
        const catId = spec.specification_category_id;
        if (!workingSpecsByCat[catId]) workingSpecsByCat[catId] = [];
        workingSpecsByCat[catId].push(spec);
    }

    // --- Loading skeleton ---
    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
                <Grid container spacing={5}>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 3 }} animation="wave" />
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                            {[0, 1, 2].map(i => (
                                <Skeleton key={i} variant="rectangular" width={80} height={60} sx={{ borderRadius: 2 }} animation="wave" />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="text" width="60%" height={44} animation="wave" />
                        <Skeleton variant="text" width="40%" height={36} animation="wave" sx={{ mt: 1 }} />
                        <Skeleton variant="text" width="100%" height={20} animation="wave" sx={{ mt: 3 }} />
                        <Skeleton variant="text" width="90%" height={20} animation="wave" />
                        <Skeleton variant="text" width="80%" height={20} animation="wave" />
                        <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 2, mt: 4 }} animation="wave" />
                    </Grid>
                </Grid>
            </Container>
        );
    }

    if (error || !product) {
        return (
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Alert severity="error">{error ?? translate('product.productNotFound')}</Alert>
                <Button startIcon={<ArrowBackIosNewIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                    {translate('common.back') ?? 'חזרה'}
                </Button>
            </Container>
        );
    }

    return (
        <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="lg">
                {/* Breadcrumbs */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <RouterLink to="/" style={{ color: 'inherit', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                        {translate('common.home') ?? 'ראשי'}
                    </RouterLink>
                    <RouterLink to="/categories" style={{ color: 'inherit', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                    >
                        {translate('common.categories') ?? 'קטגוריות'}
                    </RouterLink>
                    {categoryName && (
                        <RouterLink to={`/category/${category_id}/products`} style={{ color: 'inherit', textDecoration: 'none' }}
                            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                        >
                            {categoryName}
                        </RouterLink>
                    )}
                    <Typography variant="body2" color="text.primary" aria-current="page">
                        {productName}
                    </Typography>
                </Breadcrumbs>

                <Grid container spacing={{ xs: 4, md: 6 }}>
                    {/* ── Left: image gallery ── */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                        >
                            {/* Main image */}
                            {selectedImage ? (
                                <Box
                                    component="img"
                                    src={selectedImage}
                                    alt={productName}
                                    sx={{
                                        width: '100%',
                                        height: { xs: 280, sm: 380, md: 420 },
                                        objectFit: 'cover',
                                        borderRadius: 3,
                                        boxShadow: `0 8px 32px ${alpha(accentColor, 0.15)}`,
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: { xs: 280, sm: 380, md: 420 },
                                        borderRadius: 3,
                                        background: `linear-gradient(135deg, ${alpha(accentColor, 0.15)} 0%, ${alpha(accentColor, 0.35)} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 80, opacity: 0.4 }}>🪵</Typography>
                                </Box>
                            )}

                            {/* Thumbnail strip */}
                            {(product.images?.length ?? 0) > 1 && (
                                <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                                    {product.images!.map((url, i) => (
                                        <Box
                                            key={i}
                                            component="img"
                                            src={url}
                                            alt={`${productName}-${i}`}
                                            onClick={() => setSelectedImage(url)}
                                            sx={{
                                                width: 80,
                                                height: 60,
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                border: `2px solid ${
                                                    selectedImage === url
                                                        ? accentColor
                                                        : alpha(theme.palette.divider, 0.5)
                                                }`,
                                                transition: 'border-color 0.2s',
                                                '&:hover': { borderColor: accentColor },
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </motion.div>
                    </Grid>

                    {/* ── Right: product info ── */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
                        >
                            {/* Availability badge */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                {product.is_published === false && (
                                    <Chip label={translate('product.hiddenFromUsers')} size="small" color="default" />
                                )}
                                {product.is_available === false ? (
                                    <Chip label={translate('product.outOfStock')} size="small" color="warning" variant="outlined" />
                                ) : (
                                    <Chip
                                        icon={<CheckCircleOutlineIcon />}
                                        label={translate('product.inStock')}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                    />
                                )}
                            </Box>

                            {/* Name */}
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    lineHeight: 1.3,
                                    color: theme.palette.text.primary,
                                    fontSize: { xs: '1.6rem', md: '2rem' },
                                }}
                            >
                                {productName}
                            </Typography>

                            {/* Price */}
                            {priceDisplay && (
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    sx={{ mt: 1.5, color: accentColor, fontSize: { xs: '1.3rem', md: '1.6rem' } }}
                                >
                                    {priceDisplay}
                                </Typography>
                            )}

                            <Divider sx={{ my: 2.5 }} />

                            {/* Description */}
                            {productDescription && (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        lineHeight: 1.8,
                                        mb: 3,
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {productDescription}
                                </Typography>
                            )}

                            {/* ── Inline interactive specifications ── */}
                            {workingSpecs.length > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    {specCategories
                                        .filter(cat => workingSpecsByCat[cat.id!]?.length > 0)
                                        .map(cat => (
                                            <Box key={cat.id} sx={{ mb: 2.5 }}>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.25 }}>
                                                    {translate(cat.name as any)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {workingSpecsByCat[cat.id!].map(spec => {
                                                        const mapper = specificationMappers[spec.type];
                                                        if (!mapper) return null;
                                                        const specAddon = mapper.additionalPriceMapper(spec, quantity);
                                                        return (
                                                            <Box key={spec.id}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                                    <Typography variant="body2" fontWeight={600}>
                                                                        {translate(spec.name as any)}
                                                                    </Typography>
                                                                    {specAddon > 0 && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            +₪{specAddon.toFixed(2)}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                {mapper.widgetMapper(
                                                                    spec,
                                                                    updated => handleSpecUpdate(spec.id!, updated),
                                                                    accentColor,
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        ))}
                                    {/* Specs without a matched category */}
                                    {workingSpecs
                                        .filter(s => !specCategories.some(c => c.id === s.specification_category_id))
                                        .map(spec => {
                                            const mapper = specificationMappers[spec.type];
                                            if (!mapper) return null;
                                            return (
                                                <Box key={spec.id} sx={{ mb: 2 }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                                        {translate(spec.name as any)}
                                                    </Typography>
                                                    {mapper.widgetMapper(
                                                        spec,
                                                        updated => handleSpecUpdate(spec.id!, updated),
                                                        accentColor,
                                                    )}
                                                </Box>
                                            );
                                        })}
                                </Box>
                            )}

                            {/* ── Quantity + price summary card ── */}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    borderColor: alpha(accentColor, 0.25),
                                    backgroundColor: alpha(accentColor, 0.03),
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {translate('product.quantity') ?? 'כמות'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                            size="small"
                                            disabled={quantity <= 1}
                                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                            sx={{ border: `1px solid ${alpha(accentColor, 0.35)}`, width: 32, height: 32 }}
                                        >
                                            <RemoveIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <Typography variant="body1" fontWeight={700} sx={{ minWidth: 32, textAlign: 'center' }}>
                                            {quantity}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => setQuantity(q => q + 1)}
                                            sx={{ border: `1px solid ${alpha(accentColor, 0.35)}`, width: 32, height: 32 }}
                                        >
                                            <AddIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                </Box>

                                {product.price != null && (
                                    <>
                                        <Divider sx={{ my: 1.25 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {translate('product.basePrice') ?? 'מחיר בסיס'}
                                            </Typography>
                                            <Typography variant="body2">₪{(basePrice * quantity).toFixed(2)}</Typography>
                                        </Box>
                                        {additionsPrice > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {translate('product.additions') ?? 'תוספות'}
                                                </Typography>
                                                <Typography variant="body2" color={accentColor}>+₪{additionsPrice.toFixed(2)}</Typography>
                                            </Box>
                                        )}
                                        <Divider sx={{ my: 1 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {translate('product.total') ?? 'סה"כ'}
                                            </Typography>
                                            <Typography variant="subtitle1" fontWeight={800} color={accentColor}>
                                                ₪{totalItemPrice.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Paper>

                            {/* ── CTA buttons ── */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {orderType === 'getQuote' ? (
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<WhatsAppIcon />}
                                        fullWidth
                                        disabled={product.is_available === false}
                                        onClick={handleGetQuote}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            py: 1.5,
                                            backgroundColor: '#25D366',
                                            '&:hover': { backgroundColor: '#1ebe5d' },
                                            boxShadow: '0 4px 20px rgba(37,211,102,0.4)',
                                        }}
                                    >
                                        {translate('product.getQuote') ?? 'קבל הצעת מחיר'}
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<AddShoppingCartIcon />}
                                            fullWidth
                                            disabled={product.is_available === false}
                                            onClick={handleAddToCart}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                py: 1.5,
                                                boxShadow: `0 4px 20px ${alpha(accentColor, 0.35)}`,
                                                '&:hover': { boxShadow: `0 6px 24px ${alpha(accentColor, 0.5)}` },
                                            }}
                                        >
                                            {translate('product.addToCart') ?? 'הוסף לעגלה'}
                                        </Button>

                                        {totalItems > 0 && (
                                            <Button
                                                variant="outlined"
                                                size="large"
                                                startIcon={<ShoppingCartCheckoutIcon />}
                                                fullWidth
                                                onClick={() => setCartDialog(true)}
                                                sx={{
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    borderColor: accentColor,
                                                    color: accentColor,
                                                    '&:hover': { backgroundColor: alpha(accentColor, 0.06) },
                                                }}
                                            >
                                                {translate('product.viewCart') ?? `עגלה (${totalItems})`}
                                            </Button>
                                        )}
                                    </>
                                )}

                                <Button
                                    variant="text"
                                    size="large"
                                    startIcon={<ArrowBackIosNewIcon sx={{ transform: 'scaleX(-1)' }} />}
                                    onClick={() => navigate(-1)}
                                    fullWidth
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {translate('product.backToProducts') ?? 'חזרה למוצרים'}
                                </Button>
                            </Box>
                        </motion.div>
                    </Grid>
                </Grid>

                {/* ── Related products ── */}
                {relatedProducts.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                        <Divider sx={{ mt: 6, mb: 4 }} />
                        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
                            {translate('product.relatedProducts') ?? 'מוצרים נוספים'}
                        </Typography>
                        <Grid container spacing={3}>
                            {relatedProducts.map((related, i) => {
                                const relName = translate(related.name as any) ?? '';
                                const relLabel = related.quantity_label ? translate(related.quantity_label as any) ?? '' : '';
                                const relPrice = related.price != null
                                    ? `₪${related.price.toFixed(2)}${relLabel ? ` / ${relLabel}` : ''}`
                                    : null;
                                const relHref = `/category/${category_id}/products/${related.id}`;
                                return (
                                    <Grid item xs={12} sm={6} md={3} key={related.id ?? i}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.4, delay: i * 0.07 }}
                                            style={{ height: '100%' }}
                                        >
                                            <RouterLink to={relHref} style={{ textDecoration: 'none' }}>
                                                <Box
                                                    sx={{
                                                        borderRadius: 3,
                                                        overflow: 'hidden',
                                                        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                                                        '&:hover': {
                                                            boxShadow: `0 8px 28px ${alpha(accentColor, 0.2)}`,
                                                            transform: 'translateY(-4px)',
                                                        },
                                                    }}
                                                >
                                                    {/* Thumbnail */}
                                                    {related.images?.[0] ? (
                                                        <Box
                                                            component="img"
                                                            src={related.images[0]}
                                                            alt={relName}
                                                            sx={{ width: '100%', height: 160, objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: 160,
                                                                background: `linear-gradient(135deg, ${alpha(accentColor, 0.15)} 0%, ${alpha(accentColor, 0.35)} 100%)`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Typography sx={{ fontSize: 48, opacity: 0.4 }}>🪵</Typography>
                                                        </Box>
                                                    )}
                                                    {/* Info */}
                                                    <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                            <Typography
                                                                variant="subtitle1"
                                                                fontWeight={700}
                                                                sx={{
                                                                    color: theme.palette.text.primary,
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                {relName}
                                                            </Typography>
                                                            {relPrice && (
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    fontWeight={700}
                                                                    sx={{ color: accentColor, whiteSpace: 'nowrap', flexShrink: 0 }}
                                                                >
                                                                    {relPrice}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </RouterLink>
                                        </motion.div>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </motion.div>
                )}
            </Container>

            {/* ── "Added to cart" snackbar & dialog — cart mode only ── */}
            {orderType === 'cartOrder' && (
                <>
                    <Snackbar
                        open={cartSnackbar}
                        autoHideDuration={3000}
                        onClose={() => setCartSnackbar(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity="success" onClose={() => setCartSnackbar(false)} sx={{ width: '100%', fontWeight: 600 }}>
                            {translate('product.addedToCart') ?? 'המוצר נוסף לעגלה!'}
                        </Alert>
                    </Snackbar>

                    <CartDialog
                        open={cartDialog}
                        onClose={() => setCartDialog(false)}
                        accentColor={accentColor}
                        onCheckout={() => { setCartDialog(false); navigate('/checkout'); }}
                    />
                </>
            )}
        </Box>
    );
};

export default ProductDetailPage;
